import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ContactMethods = () => {
  const [hoveredMethod, setHoveredMethod] = useState(null);

  const contactMethods = [
    {
      id: 'email',
      title: 'Email Us',
      description: 'Get detailed responses to complex inquiries',
      value: 'connect@shutter-lifters.in',
      icon: 'Mail',
      color: 'accent',
      response: 'Within 2 hours',
      action: 'Send Email',
      href: 'https://mail.google.com/mail/?view=cm&fs=1&to=connect@shutter-lifters.in&su=Project%20Inquiry&body=Hello%20team,%20I%20would%20like%20to%20discuss%20a%20project.'
    },
    {
      id: 'phone',
      title: 'Call Direct',
      description: 'Immediate consultation and quick answers',
      value: '+917579505033',
      icon: 'Phone',
      color: 'success',
      response: 'Live support',
      action: 'Call Now',
      href: 'tel:+917579505033'
    },
    {
      id: 'chat',
      title: 'Live Chat',
      description: 'Real-time discussion about your project',
      value: 'Available 9 AM - 6 PM IST',
      icon: 'MessageCircle',
      color: 'warning',
      response: 'Instant',
      action: 'Start Chat',
      href: '#'
    },
    {
      id: 'meeting',
      title: 'Schedule Meeting',
      description: 'Book a consultation at your convenience',
      value: '30 min free consultation',
      icon: 'Calendar',
      color: 'destructive',
      response: 'Next available',
      action: 'Book Now',
      href: 'https://calendly.com/shutterlifters/30min'
    }
  ];

  const socialLinks = [
    { name: 'LinkedIn', icon: 'Linkedin', href: '#', color: 'text-blue-400' },
    { name: 'Twitter', icon: 'Twitter', href: '#', color: 'text-sky-400' },
    { name: 'GitHub', icon: 'Github', href: '#', color: 'text-gray-400' },
    { name: 'Dribbble', icon: 'Dribbble', href: '#', color: 'text-pink-400' }
  ];

  // Helper function to get the right classes based on method
  const getButtonClasses = (method) => {
    const baseClasses = "w-full transition-all duration-300";
    
    switch(method.id) {
      case 'email':
        return `${baseClasses} border-accent text-accent hover:bg-accent hover:text-white`;
      case 'phone':
        return `${baseClasses} border-green-500 text-green-500 hover:bg-green-500 hover:text-white`;
      case 'chat':
        return `${baseClasses} border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white`;
      case 'meeting':
        return `${baseClasses} border-red-500 text-red-500 hover:bg-red-500 hover:text-white`;
      default:
        return baseClasses;
    }
  };

  const getIconBgClasses = (method) => {
    switch(method.id) {
      case 'email':
        return 'bg-accent/10 border-accent/20';
      case 'phone':
        return 'bg-green-500/10 border-green-500/20';
      case 'chat':
        return 'bg-orange-500/10 border-orange-500/20';
      case 'meeting':
        return 'bg-red-500/10 border-red-500/20';
      default:
        return 'bg-accent/10 border-accent/20';
    }
  };

  const getIconColor = (method) => {
    switch(method.id) {
      case 'email':
        return 'text-accent';
      case 'phone':
        return 'text-green-500';
      case 'chat':
        return 'text-orange-500';
      case 'meeting':
        return 'text-red-500';
      default:
        return 'text-accent';
    }
  };

  return (
    <section className="py-20 bg-card/50 backdrop-blur-sm">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
            Choose Your
            <span className="text-accent"> Communication Channel</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Multiple ways to connect with our team. Pick the method that works best for your timeline and communication style.
          </p>
        </motion.div>

        {/* Contact Methods Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {contactMethods?.map((method, index) => (
            <motion.div
              key={method?.id}
              className="relative group"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.8 }}
              onHoverStart={() => setHoveredMethod(method?.id)}
              onHoverEnd={() => setHoveredMethod(null)}
            >
              <motion.div
                className="bg-background border border-border rounded-2xl p-6 h-full"
                whileHover={{
                  scale: 1.05,
                  boxShadow: "0 20px 40px rgba(0, 0, 0, 0.3)"
                }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                {/* Icon */}
                <motion.div
                  className={`w-16 h-16 rounded-xl mb-6 flex items-center justify-center border ${getIconBgClasses(method)}`}
                  animate={{
                    scale: hoveredMethod === method?.id ? 1.1 : 1
                  }}
                  transition={{ duration: 0.3 }}
                >
                  <Icon
                    name={method?.icon}
                    size={28}
                    className={getIconColor(method)}
                  />
                </motion.div>

                {/* Content */}
                <h3 className="text-xl font-bold text-foreground mb-2">
                  {method?.title}
                </h3>
                <p className="text-muted-foreground text-sm mb-4 leading-relaxed">
                  {method?.description}
                </p>

                <div className="space-y-3 mb-6">
                  <div className="flex items-center space-x-2">
                    <Icon name="Info" size={14} className="text-accent" />
                    <span className="text-sm font-medium text-foreground">
                      {method?.value}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="Clock" size={14} className="text-success" />
                    <span className="text-sm text-muted-foreground">
                      {method?.response}
                    </span>
                  </div>
                </div>

                {/* Action Button */}
                <a
                  href={method?.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <button
                    className={`px-4 py-2 rounded-lg border-2 font-medium text-sm ${getButtonClasses(method)}`}
                  >
                    {method?.action}
                  </button>
                </a>

                {/* Hover Effect Overlay */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-br from-accent/5 to-transparent rounded-2xl opacity-0 pointer-events-none"
                  animate={{
                    opacity: hoveredMethod === method?.id ? 1 : 0
                  }}
                  transition={{ duration: 0.3 }}
                />
              </motion.div>
            </motion.div>
          ))}
        </div>

        {/* Social Media Links */}
        <motion.div
          className="text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4, duration: 0.8 }}
        >
          <h3 className="text-2xl font-bold text-foreground mb-6">
            Connect on Social Media
          </h3>
          <div className="flex justify-center items-center space-x-6">
            {socialLinks?.map((social, index) => (
              <motion.a
                key={social?.name}
                href={social?.href}
                className="group"
                initial={{ opacity: 0, scale: 0 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 * index, duration: 0.5 }}
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.9 }}
              >
                <div className="w-12 h-12 bg-muted/50 rounded-full flex items-center justify-center border border-border group-hover:border-accent/50 transition-all duration-300">
                  <Icon
                    name={social?.icon}
                    size={20}
                    className={`${social?.color} group-hover:scale-110 transition-transform duration-300`}
                  />
                </div>
              </motion.a>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ContactMethods;